import JSZip from "jszip";

// Regex-based extraction (instead of DOMParser) so the module also runs in
// Node for smoke tests. OOXML text runs never nest <a:t>, so this is safe.

const collapseSpaces = (text) => text.replace(/\s+/g, " ").trim();

const slideNumber = (path) =>
  Number(path.match(/slide(\d+)\.xml$/)?.[1] || 0);

// "ppt/slides/slide3.xml" + "../media/image1.png" -> "ppt/media/image1.png"
function resolveMediaPath(slidePath, target) {
  if (!target) return null;
  if (target.startsWith("/")) return target.slice(1);
  const parts = slidePath.split("/");
  parts.pop(); // drop the file itself
  for (const segment of target.split("/")) {
    if (segment === "..") parts.pop();
    else if (segment !== "." && segment !== "") parts.push(segment);
  }
  return parts.join("/");
}

function extractRelationships(relsXml) {
  const map = {};
  const relRe = /<Relationship\b[^>]*>/g;
  const attr = (tag, name) =>
    tag.match(new RegExp(`${name}="([^"]*)"`))?.[1];
  for (const tag of relsXml.match(relRe) || []) {
    const id = attr(tag, "Id");
    const target = attr(tag, "Target");
    if (id && target) map[id] = target;
  }
  return map;
}

function extractSlideTextAndPics(slideXml) {
  const paragraphs = [];
  const picBlocks = [];
  const picRe = /<p:pic>[\s\S]*?<\/p:pic>/g;

  // Collect pictures first (with their r:embed id), then text paragraphs.
  for (const pic of slideXml.match(picRe) || []) {
    const embed = pic.match(/r:embed="([^"]+)"/)?.[1];
    if (embed) picBlocks.push(embed);
  }

  for (const rawParagraph of slideXml.split("</a:p>")) {
    const runs = [];
    const runRe = /<a:t(?:\s[^>]*)?>([\s\S]*?)<\/a:t>/g;
    let match;
    while ((match = runRe.exec(rawParagraph))) {
      runs.push(match[1]);
    }
    const text = collapseSpaces(runs.join(" "));
    if (text) paragraphs.push(text);
  }

  return { paragraphs, picBlocks };
}

const decodeXmlEntities = (text) =>
  text
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(code))
    .replace(/&amp;/g, "&");

/**
 * Parse a .pptx into ordered units (one per slide).
 * @param {File|Blob} file
 * @param {(name: string, blob: Blob) => Promise<string>} createImage registers a
 *        media blob and returns its assigned image id ("image_1", ...).
 * @returns {Promise<{kind: "pptx", units: Array<{index: number, label: string, text: string, imageIds: string[]}>}>}
 */
export async function parsePptx(file, createImage) {
  const zip = await JSZip.loadAsync(await file.arrayBuffer());

  const slidePaths = Object.keys(zip.files)
    .filter((name) => /^ppt\/slides\/slide\d+\.xml$/.test(name))
    .sort((a, b) => slideNumber(a) - slideNumber(b));

  if (!slidePaths.length) {
    throw new Error("No slides found in the presentation.");
  }

  const units = [];
  let mediaCounter = 0;
  const nameFor = (path) =>
    `slide${slideNumber(path)}_${++mediaCounter}_${path
      .split("/")
      .pop()}`;

  for (const slidePath of slidePaths) {
    const slideXml = decodeXmlEntities(
      await zip.file(slidePath).async("string")
    );
    const relsPath = `ppt/slides/_rels/${slidePath
      .split("/")
      .pop()}.rels`;
    let rels = {};
    if (zip.file(relsPath)) {
      rels = extractRelationships(
        await zip.file(relsPath).async("string")
      );
    }

    const { paragraphs, picBlocks } =
      extractSlideTextAndPics(slideXml);

    const imageIds = [];
    // a <p:pic> can reference its blip more than once; keep first per slide
    for (const embedId of new Set(picBlocks)) {
      const mediaPath = resolveMediaPath(slidePath, rels[embedId]);
      const entry = mediaPath && zip.file(mediaPath);
      if (!entry) continue;
      const blob = await entry.async("blob");
      imageIds.push(await createImage(nameFor(slidePath), blob));
    }

    // Speaker notes carry answers/explanations in many decks.
    const notesTarget = Object.entries(rels).find(([, target]) =>
      target.includes("notesSlide")
    )?.[1];
    const notesPath =
      notesTarget && resolveMediaPath(slidePath, notesTarget);
    if (notesPath && zip.file(notesPath)) {
      const notesXml = decodeXmlEntities(
        await zip.file(notesPath).async("string")
      );
      const noteRuns = [];
      const runRe = /<a:t(?:\s[^>]*)?>([\s\S]*?)<\/a:t>/g;
      let match;
      while ((match = runRe.exec(notesXml))) {
        const text = collapseSpaces(match[1]);
        if (text) noteRuns.push(text);
      }
      if (noteRuns.length) {
        paragraphs.push(`Speaker notes: ${noteRuns.join(" ")}`);
      }
    }

    units.push({
      index: units.length,
      label: `slide ${slideNumber(slidePath)}`,
      text: paragraphs.join("\n"),
      imageIds
    });
  }

  return { kind: "pptx", units };
}
